import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

/**
 * Inline product create — used from the Order form when a required product
 * isn't in the Product Master yet. On save it inserts a Product and returns
 * the freshly-created row so the caller can immediately select it in the
 * current line.
 */
export default function QuickProductDialog({ open, onOpenChange, units, onCreated, initialName = "" }) {
  const [name, setName] = useState(initialName);
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("PCS");
  const [gst, setGst] = useState(18);
  const [stock, setStock] = useState(0);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setSku(""); setPrice(""); setUnit("PCS"); setGst(18); setStock(0);
    }
  }, [open, initialName]);

  const auto = (n) => {
    if (!sku) setSku(n.toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 20));
  };

  const submit = async () => {
    if (!name.trim())  return toast.error("Product name required");
    if (!sku.trim())   return toast.error("SKU required");
    if (!price || +price <= 0) return toast.error("Price must be > 0");
    setBusy(true);
    try {
      const r = await api.post("/products", {
        name: name.trim(), sku: sku.trim(),
        price: +price, gst: +gst, stock: +stock || 0, unit,
      });
      toast.success(`Product "${r.data.name}" added to master`);
      onCreated(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quick-product-dialog">
        <DialogHeader>
          <DialogTitle>Quick create product</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">The product will be saved to your Product Master and auto-selected in the current order line.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">NAME</Label>
              <Input value={name}
                onChange={(e) => { setName(e.target.value); auto(e.target.value); }}
                placeholder="e.g. Copper wire 1.5mm"
                data-testid="qp-name" />
            </div>
            <div>
              <Label className="text-xs">SKU</Label>
              <Input value={sku} onChange={(e) => setSku(e.target.value.toUpperCase())} className="font-mono" data-testid="qp-sku" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">PRICE (₹)</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="qp-price" />
            </div>
            <div>
              <Label className="text-xs">UNIT</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="qp-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(units || []).map((u) => (
                    <SelectItem key={u.key} value={u.label}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">GST %</Label>
              <Input type="number" value={gst} onChange={(e) => setGst(+e.target.value)} data-testid="qp-gst" />
            </div>
            <div>
              <Label className="text-xs">STOCK</Label>
              <Input type="number" value={stock} onChange={(e) => setStock(+e.target.value)} data-testid="qp-stock" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="qp-submit">
            {busy ? "Saving…" : "Create & add to order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
